import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import VideoCard from '@/pages/home/components/VideoCard';
import { videos, videoCategories, siteInfo } from '@/mocks/videos';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((v) => v.id === id);

  if (!video) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">视频未找到</h1>
          <p className="text-foreground-500 mb-6">您要查看的视频不存在或已下架</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedVideos = videos
    .filter((v) => v.categorySlug === video.categorySlug && v.id !== video.id)
    .slice(0, 6);

  const otherVideos = videos
    .filter((v) => v.categorySlug !== video.categorySlug)
    .slice(0, 6);

  const pageUrl = `${siteInfo.url}/article/${video.id}`;
  const category = videoCategories.find((c) => c.slug === video.categorySlug);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: siteInfo.url },
      { '@type': 'ListItem', position: 2, name: video.category, item: `${siteInfo.url}/category/${video.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: video.title, item: pageUrl },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    url: pageUrl,
    datePublished: `${video.year}-01-01`,
    author: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${video.title} - ${siteInfo.name}`,
    description: `${video.title}，${video.description}。${siteInfo.name}提供${video.title}高清免费在线观看，流畅不卡顿。`,
    url: pageUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: siteInfo.name,
      url: siteInfo.url,
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100/50 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
              <nav className="flex items-center gap-2 text-sm text-foreground-500" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <span className="text-foreground-300">/</span>
                <Link to={`/category/${video.categorySlug}`} className="hover:text-primary-600 transition-colors cursor-pointer">{video.category}</Link>
                <span className="text-foreground-300">/</span>
                <span className="text-foreground-900 font-medium line-clamp-1">{video.title}</span>
              </nav>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="lg:w-[300px] shrink-0">
                <div className="sticky top-24">
                  <div className="relative aspect-[2/3] rounded-lg overflow-hidden bg-background-200 mb-4">
                    <img
                      src={video.cover}
                      alt={video.title}
                      title={`${video.title} - 桃花视频免费在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                    {video.isHot && (
                      <span className="absolute top-3 left-3 px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full">热播</span>
                    )}
                    {video.isNew && (
                      <span className="absolute top-3 left-16 px-3 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full">新上</span>
                    )}
                  </div>
                  <div className="flex items-center justify-center gap-3">
                    <button className="flex-1 px-5 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-play-fill text-lg"></i>
                      </div>
                      立即播放
                    </button>
                    <button className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap" aria-label="收藏">
                      <i className="ri-heart-line text-lg text-foreground-600"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  {video.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <span className="flex items-center gap-1 px-3 py-1.5 bg-accent-100 text-accent-800 rounded-full text-sm font-medium">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-xs"></i>
                    </div>
                    {video.rating}
                  </span>
                  <span className="px-3 py-1.5 bg-background-100 text-foreground-700 rounded-full text-sm">{video.year}</span>
                  <span className="px-3 py-1.5 bg-background-100 text-foreground-700 rounded-full text-sm">{video.region}</span>
                  <span className="px-3 py-1.5 bg-background-100 text-foreground-700 rounded-full text-sm">{video.episodeCount}</span>
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="flex items-center gap-1 px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-full text-sm hover:bg-secondary-200 transition-colors cursor-pointer"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${category.icon} text-xs`}></i>
                      </div>
                      {category.name}
                    </Link>
                  )}
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {video.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-background-100 text-foreground-600 rounded-md text-xs">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="bg-background-100/50 rounded-lg p-5 mb-6 border border-background-200/70">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-information-line text-primary-500"></i>
                    </div>
                    <h2 className="font-heading font-semibold text-foreground-900">剧情简介</h2>
                  </div>
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{video.description}</p>
                </div>

                <div className="bg-background-100/50 rounded-lg p-5 mb-6 border border-background-200/70">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-refresh-line text-accent-500"></i>
                    </div>
                    <h2 className="font-heading font-semibold text-foreground-900">更新状态</h2>
                  </div>
                  <p className="text-sm text-foreground-600">
                    <span className="text-accent-600 font-medium">{video.updateInfo}</span>
                    <span className="mx-2 text-foreground-300">|</span>
                    来源：红桃视频、樱桃视频、茄子视频、糖心视频等多平台聚合
                  </p>
                </div>
              </div>
            </div>
          </div>

          {relatedVideos.length > 0 && (
            <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100/50">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                    <i className="ri-links-line text-lg text-primary-600"></i>
                  </div>
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
                    同分类推荐
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                  {relatedVideos.map((v) => (
                    <VideoCard key={v.id} video={v} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {otherVideos.length > 0 && (
            <section className="px-4 md:px-6 py-8 md:py-10">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
                    <i className="ri-thumb-up-line text-lg text-accent-600"></i>
                  </div>
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900">
                    猜你喜欢
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                  {otherVideos.map((v) => (
                    <VideoCard key={v.id} video={v} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}